import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";
import { 
  Bell, X, Calendar, Trophy, MessageCircle, Star, 
  CheckCircle, AlertCircle, Info
} from "lucide-react";

const NotificationBell = ({ token }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.log("Could not fetch notifications");
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.log("Could not mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.log("Could not mark all as read");
    }
  };

  const getIcon = (type) => {
    const icons = {
      event_reminder: Calendar,
      event_update: AlertCircle,
      new_rsvp: CheckCircle,
      rating_received: Star,
      message: MessageCircle,
      tournament: Trophy,
      info: Info,
    };
    const Icon = icons[type] || Bell;
    return <Icon className="w-4 h-4" />;
  };

  const getIconColor = (type) => {
    const colors = {
      event_reminder: "text-blue-400",
      event_update: "text-yellow-400",
      new_rsvp: "text-green-400",
      rating_received: "text-[#7a6200]",
      message: "text-purple-400",
      tournament: "text-[#7a6200]",
      info: "text-[#A1A1AA]",
    };
    return colors[type] || "text-[#A1A1AA]";
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-[#A1A1AA]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#7a6200] text-black text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-hidden rounded-lg border border-white/10 bg-[#0A0A0A] shadow-xl z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <h3 className="font-bebas text-lg tracking-wider text-white">
              NOTIFICATIONS
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-[#7a6200] text-xs font-ubuntu hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-[#52525B] mx-auto mb-2" />
                <p className="text-[#A1A1AA] text-sm font-ubuntu">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                  className={`p-3 border-b border-white/5 cursor-pointer transition-colors ${
                    notification.read ? "opacity-60" : "bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center ${getIconColor(notification.type)}`}>
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-ubuntu font-medium truncate">
                        {notification.title}
                      </p>
                      <p className="text-[#A1A1AA] text-xs font-ubuntu line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-[#52525B] text-xs mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full bg-[#7a6200] flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-white/10">
              <Button
                variant="ghost"
                className="w-full text-[#7a6200] text-sm hover:bg-white/5"
                onClick={() => {
                  setIsOpen(false);
                  // Navigate to notifications page if needed
                }}
              >
                View All
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
